/*
  Algolia search — entry point.

  Reads the config data island rendered by snippets/search.algolia.liquid, registers the app
  block's hooks and wires the feature modules (card, enrich, facets, wishlist, notify,
  redirect, loader). Everything template-specific comes from the data island; there is no Liquid
  here.

  Registration has to happen inside the `algolia.hooks.initialize` listener: the app only
  honours hooks registered before it initialises, and a hook added afterwards is silently
  ignored.

  Classic script (not a module) on purpose — a deferred module would run too late to catch that
  event.
*/
;(function () {
  'use strict'

  // Behaviour constants: developer-facing, so they live here rather than in the data island.
  const MIN_QUERY_LENGTH = 3
  /* How long the search panel waits after the last keystroke before it asks Algolia. The old
     theme's "Search debounce delay" setting, 400ms on production. See `beforeAutocompleteOptions`. */
  const AUTOCOMPLETE_DEBOUNCE_MS = 400
  const DETACHED_MEDIA_QUERY = '(max-width: 768px)'

  /* Each call restarts the timer and returns a promise that settles only if no later call comes
     within `wait` — so a burst of keystrokes resolves once, for the last of them. */
  function debouncePromise(fn, wait) {
    let timer
    return function () {
      const args = arguments
      clearTimeout(timer)
      return new Promise(function (resolve) {
        timer = setTimeout(function () {
          resolve(fn.apply(null, args))
        }, wait)
      })
    }
  }

  const configEl = document.getElementById('search-algolia-config')
  if (!configEl) return

  let config
  try {
    config = JSON.parse(configEl.textContent)
  } catch (e) {
    console.error('[search.algolia] could not parse the config data island', e)
    return
  }

  // Facets only render on the search and collection templates, so search.algolia.facets.js is
  // only loaded there — everywhere else the autocomplete panel is the whole surface.
  // Same gating as the facets module: only the results templates need this one.
  const NO_EMPTY_STATE = {
    template: null,
    apply: function () {}
  }

  const NO_FACETS = {
    shapeOptions: function (options) {
      return options
    },
    revealSoon: function () {},
    ensureSortLabel: function () {}
  }

  const PZSearch = window.PZSearch
  const enrich = PZSearch.createEnricher(config)
  const facets = PZSearch.createFacets ? PZSearch.createFacets(config) : NO_FACETS
  const notify = PZSearch.createNotify(config)
  const refreshWishlist = PZSearch.createWishlist()
  const redirect = PZSearch.createRedirect(config)
  const loader = PZSearch.createLoader(config)
  const recentSearches = PZSearch.createRecentSearches(config)
  const emptyState = PZSearch.createNoResults ? PZSearch.createNoResults() : NO_EMPTY_STATE
  const mainTemplate = PZSearch.createMainTemplate ? PZSearch.createMainTemplate(config) : null

  // Every rule redirect leaves under the navigation cover, wherever it was picked up.
  function applyRedirect(result, query) {
    if (!redirect.apply(result, query)) return false
    loader.hold()
    return true
  }

  /*
    Consumed cross-file by the LCP preload snippet, which already queries the index from <head>
    on the results page and is therefore the earliest place a rule redirect can be seen.
    Exported explicitly so that dependency is visible instead of implicit.
  */
  PZSearch.applyRedirect = applyRedirect
  // Same consumer: once it knows there is no redirect, the search page can be shown.
  PZSearch.hideSearchLoader = loader.hide

  // Runs before any hook: a hand-off left over from an unrelated page must not prefill a search.
  redirect.consumeStaleHandoff()

  /*
    The enrich module dispatches this once a fragment says a card is sold out. Listening here
    rather than inside enrich.js keeps the two modules independent — enrich knows availability,
    notify knows the back-in-stock app.
  */
  document.addEventListener('pz:search:sold-out', function (event) {
    notify(event.detail.card)
  })

  document.addEventListener('algolia.hooks.initialize', function () {
    const algolia = window.algoliaShopify
    if (!algolia || !algolia.hooks) return

    applyAppTranslations(algolia)

    const renderItem = PZSearch.createRenderItem(config)

    // One index name for every surface, from the data island. The old theme resolved this in
    // three separate places and they were free to drift.
    algolia.hooks.registerHook('beforeInstantSearchConfigurationOptions', function (options) {
      const appIndexName = options.indexName
      options.indexName = config.indexName
      rekeyRouting(options, appIndexName, config.indexName)
      return options
    })

    // Facet list, facet operator and sort-order labels.
    algolia.hooks.registerHook('beforeInstantSearchOptions', function (options) {
      return facets.shapeOptions(options)
    })

    // The value search inside a searchable facet ("Search for Brand"), translated.
    if (facets.searchablePlaceholder) {
      algolia.hooks.registerHook('beforeInstantSearchFacetSearchablePlaceholder', facets.searchablePlaceholder)
      algolia.hooks.registerHook('beforeISFacetSearchableNoResultsString', facets.searchableNoResults)
    }

    /*
      The results layout itself. The app's own has no panel around the facets, which on a phone
      left the filter list open with no way to close it — see search.algolia.main-template.js.
    */
    if (mainTemplate) {
      algolia.hooks.registerHook('beforeInstantSearchMainTemplate', mainTemplate.template)
      mainTemplate.bind()
    }

    if (facets.statsTemplate) {
      algolia.hooks.registerHook('beforeInstantSearchStatsTemplate', function (defaultTemplate, data, html) {
        /*
          The stats template renders on every InstantSearch render, zero hits included — the one
          notification that still fires when no card does. That makes it the trigger for the empty
          state, instead of the fixed timeout this used to run on.
        */
        scheduleEmptyState()
        // The mobile footer's count is the same number, rendered in our own layout; and the
        // selected-filter chips lose the stray colon the app renders into them.
        if (mainTemplate) requestAnimationFrame(mainTemplate.afterRender)
        return facets.statsTemplate(defaultTemplate, data, html)
      })
    }

    // Our own empty state, or nothing when a Search CTA banner is on the page.
    if (emptyState.template) {
      algolia.hooks.registerHook('beforeInstantSearchNoResultTemplate', emptyState.template)
    }

    algolia.hooks.registerHook('beforeAutocompleteOptions', function (options) {
      /*
        Two options replacing hand-written DOM work from the old theme: `detachedMediaQuery`
        gives the mobile overlay the app's own detached search button (it used to dispatch
        synthetic clicks at the header inputs), and the initial query prefills the input — see
        the prefill note in search.algolia.redirect.js.

        Prefill goes through `initialState.query`, autocomplete-js's own option. `initialQuery`
        is not one: it belongs to the pre-app-block integration the old theme still carries in
        assets/algolia_autocomplete.js.liquid, a file that hasn't been loaded since 2025 —
        setting it here leaves the input empty on a /search?q=… page.
      */
      options.detachedMediaQuery = DETACHED_MEDIA_QUERY
      options.placeholder = config.labels.searchPlaceholder

      // The phone overlay's "Cancel" is autocomplete-js's own string, English unless passed in.
      options.translations = Object.assign({}, options.translations, {
        detachedCancelButtonText: config.labels.cancel
      })

      const initial = redirect.initialQuery()
      if (initial) {
        options.initialState = Object.assign({}, options.initialState, { query: initial })
      }

      /*
        What the panel costs. The products, collections and suggestions all come from the app's
        *plugins*; the root `getSources` adds no source of its own. Autocomplete waits for the root
        promise before it asks the plugins for their items, so a root `getSources` that resolves
        late is a debounce for the whole panel, and one that resolves nothing below the length
        floor keeps the plugins quiet too. It resolves to an empty list on purpose: a products
        source here would query the products index a second time in the same request.

        Without it every keystroke went out as its own request of three queries — "vitamin" cost
        7 requests and 21 queries where the old theme sent 1 and 3, measured on both themes
        2026-10-26. The account's daily search requests tripled the day this theme went live
        (2026-10-20). The old theme lost this twice before, each time for ~70% more requests.

        (An earlier note here said this could not be done because the app sets `getSources` after
        this hook. It does not override it — measured on the old theme, same app block.)
      */
      const debounced = debouncePromise(() => [], AUTOCOMPLETE_DEBOUNCE_MS)

      options.getSources = function (params) {
        const query = (params && params.query) || ''
        if (query.length < MIN_QUERY_LENGTH) return false

        return debounced()
      }

      /*
        Don't show the panel for one or two characters — on an index this size those results are
        noise. This replicates autocomplete-js's default (open when any source has items) with the
        length floor added.
      */
      options.shouldPanelOpen = function (params) {
        const state = params && params.state
        if (!state || !state.query || state.query.length < MIN_QUERY_LENGTH) return false

        return (state.collections || []).some(function (collection) {
          return collection && collection.items && collection.items.length > 0
        })
      }

      return options
    })

    /*
      Panel layout. Reproduces the app's own two-section layout — suggestions, collections, articles
      and pages on the left, products on the right — and adds the recent-searches block the old theme
      had. Written out rather than wrapped because the hook hands us the rendered `elements`, not the
      default template's markup.
    */
    algolia.hooks.registerHook(
      'beforeAutocompleteMainTemplate',
      function (_defaultTemplate, templateOptions, elements, displaySuggestions) {
        const html = templateOptions.html
        const recent = recentSearches.list()

        return html`
          <div class="aa-PanelLayout aa-Panel--scrollable">
            <div class="aa-PanelSections">
              <div class="aa-PanelSection--left">
                ${displaySuggestions &&
                html`
                  <div class="aa-SourceHeader">
                    <span class="aa-SourceHeaderTitle"
                      >${(config.translations && config.translations.suggestions) ||
                      algolia.translations.suggestions}</span
                    >
                    <div class="aa-SourceHeaderLine"></div>
                  </div>
                  ${elements.querySuggestionsPlugin}
                `}
                ${elements.collections} ${elements.articles} ${elements.pages}
                ${recent.length
                  ? html`
                      <div class="aa-SourceHeader">
                        <span class="aa-SourceHeaderTitle">${config.labels.recentSearches}</span>
                        <div class="aa-SourceHeaderLine"></div>
                      </div>
                      <div class="aa-Source" data-autocomplete-source-id="recentSearches">
                        ${recent.map(function (term) {
                          return recentSearches.renderItem(term, html)
                        })}
                      </div>
                    `
                  : ''}
                ${elements.redirectUrlPlugin}
              </div>
              <div class="aa-PanelSection--right">${elements.products}</div>
            </div>
          </div>
        `
      }
    )

    /*
      The one hook that sees a raw Autocomplete response, so it is where a Rules redirect can be
      picked up while typing. The old theme replaced the whole products source to get at it.
    */
    algolia.hooks.registerHook('beforeAutocompleteProductTransformResponseHits', function (hits, results) {
      const result = firstResult(results)
      if (result) applyRedirect(result, result.query)
      return hits
    })

    algolia.hooks.registerHook(
      'beforeAutocompleteProductTemplate',
      function (_defaultTemplate, templateOptions, distinct, itemLink) {
        const { html, item, components } = templateOptions
        schedulePass('.aa-Panel', item.id)
        return renderItem(item, distinct, html, components, itemLink)
      }
    )

    algolia.hooks.registerHook(
      'beforeInstantSearchProductTemplate',
      function (_defaultTemplate, hit, html, components) {
        schedulePass('.ais-page', hit.id)
        return renderItem(hit, hit._distinct, html, components)
      }
    )
  })

  /*
    Strings the app block renders itself (sort labels, "clear all", the no-results heading).
    Applied as a map from the data island instead of the old theme's ten Liquid-interpolated
    assignments.
  */
  function applyAppTranslations(algolia) {
    const target = algolia.shopify && algolia.shopify.market && algolia.shopify.market.translations
    if (!target || !config.translations) return

    Object.keys(config.translations).forEach(function (key) {
      const value = config.translations[key]
      if (value) target[key] = value
    })
  }

  /*
    The app's URL routing reads the UI state under the index name *it* resolved from its markets
    mapping (`shopify_products` + suffix). When that differs from ours, the state lives under our
    name, the app's stateToRoute reads `undefined.query` and the page renders facets but no cards.
    That happened on /ar for the MENA Europe market, which had no Arabic row in the mapping
    (PHARM-1030). Translating the key both ways keeps the page working whatever the mapping says.
  */
  function rekeyRouting(options, appIndexName, indexName) {
    const mapping = options.routing && options.routing.stateMapping
    if (!mapping || appIndexName === indexName) return

    options.routing.stateMapping = {
      stateToRoute: function (uiState) {
        const state = Object.assign({}, uiState)
        state[appIndexName] = uiState[indexName] || {}
        return mapping.stateToRoute(state)
      },
      routeToState: function (route) {
        const state = mapping.routeToState(route)
        const result = {}
        result[indexName] = state[appIndexName]
        return result
      }
    }
  }

  // The app has passed this argument as an array of results and as the raw response, depending
  // on version — accept both rather than depending on one shape.
  function firstResult(results) {
    if (!results) return null
    if (Array.isArray(results)) return results[0]
    if (Array.isArray(results.results)) return results.results[0]
    return results
  }

  /*
    Fallback for the LCP images: when Algolia does not send __position, renderItem cannot tell
    which cards are in the first row, so every image ends up lazy. Promote the first four once
    they exist. Only does anything when the attribute was missing.
  */
  function promoteFirstRowImages() {
    const imgs = document.querySelectorAll('.ais-page .custom-algolia-item img')
    if (!imgs.length || imgs[0].getAttribute('loading') === 'eager') return

    for (let i = 0; i < Math.min(4, imgs.length); i++) {
      imgs[i].setAttribute('loading', 'eager')
      imgs[i].setAttribute('fetchpriority', 'high')
    }
  }

  /*
    The product-template hook is the render notification.

    The old theme keyed this off an `algolia.instantSearch.onRender` document event. Using the
    hook instead means the trigger doesn't depend on the app block's event vocabulary at all —
    it fires exactly when cards render, on both surfaces, and survives app updates that rename
    or drop events. We collect ids per container and run one pass per render batch.
  */
  const pending = new Map() // container -> Set of ids

  function schedulePass(container, id) {
    if (id == null) return

    let entry = pending.get(container)
    if (!entry) {
      entry = { ids: new Set(), raf: 0, timer: 0 }
      pending.set(container, entry)
    }
    entry.ids.add(String(id))

    cancelAnimationFrame(entry.raf)
    clearTimeout(entry.timer)

    function run() {
      const ids = Array.from(entry.ids)
      entry.ids.clear()
      if (!ids.length) return

      if (container === '.ais-page') {
        promoteFirstRowImages()
        facets.revealSoon()
        facets.ensureSortLabel()
        emptyState.apply()
      }

      // Swym only decorates the cards it can see, so it has to re-scan after every re-render.
      refreshWishlist()
      enrich(ids, container)
    }

    /*
      rAF then setTimeout so the pass starts only after the freshly rendered cards have painted —
      a bare setTimeout can fire before first paint, and in the old theme that cost roughly 1.9s
      of LCP render delay.

      Chrome does not run rAF callbacks in a hidden tab, so gate on visibility: with nothing being
      painted there is no paint to order against, and going straight to a timeout keeps a search
      page opened in a background tab from sitting there unenriched.
    */
    if (document.visibilityState === 'visible') {
      entry.raf = requestAnimationFrame(function () {
        entry.timer = setTimeout(run, 0)
      })
    } else {
      entry.timer = setTimeout(run, 0)
    }
  }

  /*
    Facets and the sort control render even when a query returns no hits, in which case the
    product-template hook never fires and the pass above never runs. Kick the reveal once on
    load so they can't stay hidden behind `.ais-facets-ready`.
  */
  if (config.pageType === 'search' || config.pageType === 'collection') {
    document.addEventListener('DOMContentLoaded', function () {
      facets.revealSoon()
      facets.ensureSortLabel()
    })
  }

  /*
    Deferred the same way as the enrich pass: the stats template runs *during* a render, so
    `.ais-Hits--empty` isn't in the DOM yet when it fires. rAF then a timeout puts us after paint —
    with the hidden-tab escape hatch, since Chrome doesn't run rAF callbacks in a background tab.
  */
  let emptyStateRaf = 0
  let emptyStateTimer = 0

  function scheduleEmptyState() {
    cancelAnimationFrame(emptyStateRaf)
    clearTimeout(emptyStateTimer)

    if (document.visibilityState === 'visible') {
      emptyStateRaf = requestAnimationFrame(function () {
        emptyStateTimer = setTimeout(emptyState.apply, 0)
      })
    } else {
      emptyStateTimer = setTimeout(emptyState.apply, 0)
    }
  }
})()
